import { API_CONFIG, fetchAPI } from './api';

export type AccountSettingsResponse = {
  success: boolean;
  error?: string;
  account?: {
    email: string;
    firstName: string | null;
    lastName: string | null;
    accountName?: string | null;
    credits: number;
    contactCredits: number;
    contactCreditsResetAt: string;
    resupplyDay: number;
    nextResupplyAt: string;
    timezone: string;
    ghlLocationId?: string | null;
    ghlSendAsUserEmail?: string | null;
    ghlSendAsLocationId?: string | null;
    instantlyCampaignIds?: string[];
  };
  ghlSendAsOptions?: Array<{
    locationId: string;
    name: string;
    active?: boolean;
  }>;
  schedules?: Array<{
    scheduleId: string;
    name: string;
    status: string;
    scheduleDays: string[];
    scheduleHour: number;
    scheduleMinute?: number;
    timezone: string;
    creditLimit: number;
    researchMode?: string;
    instantlyCampaignId?: string | null;
    instantlyCampaignName?: string | null;
    targetGhlLocationId?: string | null;
    nextRunAt: string | null;
    updatedAt: string | null;
  }>;
  integrations?: {
    webhook: IntegrationField;
    pitToken: IntegrationField;
    locationId: IntegrationField;
    linkedInConnectionCookie: IntegrationField;
    instagramConnectionCookie: IntegrationField;
    linkedInCommentAgentId: IntegrationField;
    instagramCommentAgentId: IntegrationField;
  };
};

export type IntegrationField = {
  hasValue: boolean;
  valid: boolean;
  value: string | null;
  maskedValue: string | null;
};

type AuthInput = {
  token: string;
  email: string;
  baseUrl?: string;
};

function usersApi(baseUrl?: string): string {
  return baseUrl || API_CONFIG.USERS_API;
}

export async function getAccountSettings(input: AuthInput): Promise<AccountSettingsResponse> {
  return fetchAPI<AccountSettingsResponse>(usersApi(input.baseUrl), {
    method: 'POST',
    headers: { Authorization: `Bearer ${input.token}` },
    body: JSON.stringify({
      action: 'get_account_settings',
      email: input.email,
    }),
  });
}

export async function updateAccountProfile(input: AuthInput & { firstName: string; lastName: string }) {
  return fetchAPI<{ success: boolean; error?: string; profile?: { firstName: string | null; lastName: string | null } }>(
    usersApi(input.baseUrl),
    {
      method: 'POST',
      headers: { Authorization: `Bearer ${input.token}` },
      body: JSON.stringify({
        action: 'update_account_profile',
        email: input.email,
        firstName: input.firstName,
        lastName: input.lastName,
      }),
    }
  );
}

export async function changeAccountPassword(input: AuthInput & { currentPassword: string; newPassword: string }) {
  return fetchAPI<{ success: boolean; error?: string }>(usersApi(input.baseUrl), {
    method: 'POST',
    headers: { Authorization: `Bearer ${input.token}` },
    body: JSON.stringify({
      action: 'change_password',
      email: input.email,
      currentPassword: input.currentPassword,
      newPassword: input.newPassword,
    }),
  });
}

export async function setGhlSendAsUser(input: AuthInput & { ghlSendAsUserEmail: string | null }) {
  return fetchAPI<{ success: boolean; error?: string; ghlSendAsUserEmail?: string | null }>(usersApi(input.baseUrl), {
    method: 'POST',
    headers: { Authorization: `Bearer ${input.token}` },
    body: JSON.stringify({
      action: 'set_ghl_send_as',
      email: input.email,
      ghlSendAsUserEmail: input.ghlSendAsUserEmail,
    }),
  });
}

export async function setGhlSendAsLocation(input: AuthInput & { ghlSendAsLocationId: string | null }) {
  return fetchAPI<{ success: boolean; error?: string; ghlSendAsLocationId?: string | null }>(usersApi(input.baseUrl), {
    method: 'POST',
    headers: { Authorization: `Bearer ${input.token}` },
    body: JSON.stringify({
      action: 'set_ghl_send_as_location',
      email: input.email,
      ghlSendAsLocationId: input.ghlSendAsLocationId,
    }),
  });
}

export async function setMyGhlConnection(
  input: AuthInput & { locationId: string; pit: string; accountName?: string | null }
) {
  return fetchAPI<{
    success: boolean;
    error?: string;
    ghlLocationId?: string | null;
    accountName?: string | null;
  }>(usersApi(input.baseUrl), {
    method: 'POST',
    headers: { Authorization: `Bearer ${input.token}` },
    body: JSON.stringify({
      action: 'set_my_ghl_connection',
      email: input.email,
      locationId: input.locationId,
      pit: input.pit,
      accountName: input.accountName ?? null,
    }),
  });
}

export async function testMyGhlConnection(
  input: AuthInput & {
    locationId?: string;
    pit?: string;
    contactName?: string;
    contactEmail?: string;
    companyName?: string;
  }
) {
  return fetchAPI<{
    success: boolean;
    error?: string;
    statusCode?: number;
    locationId?: string;
    requestPayload?: Record<string, unknown>;
    response?: unknown;
  }>(usersApi(input.baseUrl), {
    method: 'POST',
    headers: { Authorization: `Bearer ${input.token}` },
    body: JSON.stringify({
      action: 'test_my_ghl_connection',
      email: input.email,
      locationId: input.locationId,
      pit: input.pit,
      contactName: input.contactName,
      contactEmail: input.contactEmail,
      companyName: input.companyName,
    }),
  });
}

export async function listMyScheduleRuns(input: AuthInput & { limit?: number; status?: string; includeRetries?: boolean }) {
  return fetchAPI<{ success: boolean; items?: any[]; error?: string }>(usersApi(input.baseUrl), {
    method: 'POST',
    headers: { Authorization: `Bearer ${input.token}` },
    body: JSON.stringify({
      action: 'list_my_schedule_runs',
      email: input.email,
      limit: input.limit ?? 500,
      status: input.status,
      includeRetries: input.includeRetries === true,
    }),
  });
}

export async function updateMySchedule(
  input: AuthInput & {
    scheduleId: string;
    updates: Record<string, any>;
  }
) {
  return fetchAPI<{ success: boolean; item?: any; error?: string }>(usersApi(input.baseUrl), {
    method: 'POST',
    headers: { Authorization: `Bearer ${input.token}` },
    body: JSON.stringify({
      action: 'update_my_schedule',
      email: input.email,
      scheduleId: input.scheduleId,
      updates: input.updates,
    }),
  });
}

export async function deleteMySchedule(
  input: AuthInput & {
    scheduleId: string;
  }
) {
  return fetchAPI<{ success: boolean; error?: string }>(usersApi(input.baseUrl), {
    method: 'POST',
    headers: { Authorization: `Bearer ${input.token}` },
    body: JSON.stringify({
      action: 'delete_my_schedule',
      email: input.email,
      scheduleId: input.scheduleId,
    }),
  });
}
